import { NOTIFICATION_KINDS, type NotificationCounts, type NotificationSetting, type NotificationsRemoved, type NotificationView } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../api';
import { fmt } from '../format';
import { clearAllQuestion, removedText } from '../notifications';
import { navigate } from '../router';
import { Empty, ErrorBanner, SellerChip, Spinner, useLoad, useToast } from '../ui';
import { Icon, type IconName } from '../ui/icons';

/** The bell in the top bar listens for this to count again at once. */
export const NOTIFICATIONS_CHANGED = 'wms:notifications';
const changed = () => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));

const ICONS: Readonly<Record<string, IconName>> = {
  inbound: 'inbox',
  order: 'list',
  marketplace: 'plug',
  stock: 'stock',
  invoice: 'receipt',
  ops: 'pulse',
};

/**
 * Bildirimler: what happened that this person should know, newest first; a tap opens the screen.
 * Each person deletes their own: one from its row, the read ones, or all of them (asked first).
 */
export function Notifications() {
  const toast = useToast();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  // deleted from their row, gone from the list before the server answers
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const list = useLoad(() => api<NotificationView[]>('GET', `/api/notifications?limit=100${unreadOnly ? '&unread=true' : ''}`), [unreadOnly], 30_000);
  // the list shows the latest 100: the buttons go by the counts of all of them
  const counts = useLoad(() => api<NotificationCounts>('GET', '/api/notifications/count'), [], 30_000);
  const shown = list.data?.filter((n) => !removed.has(n.id));
  const unread = counts.data?.unread ?? (shown ?? []).filter((n) => !n.readAt).length;
  const total = counts.data?.total ?? shown?.length ?? 0;

  const refresh = () => {
    changed();
    void list.reload();
    void counts.reload();
  };
  const markAll = async () => {
    try {
      await api('POST', '/api/notifications/read', {});
      refresh();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };
  const open = async (n: NotificationView) => {
    if (!n.readAt) {
      await api('POST', '/api/notifications/read', { ids: [n.id] }).catch(() => undefined);
      changed();
    }
    if (n.link) navigate(n.link.slice(1));
    else refresh();
  };
  const remove = async (n: NotificationView) => {
    setRemoved((all) => new Set(all).add(n.id));
    try {
      const answer = await api<NotificationsRemoved>('DELETE', '/api/notifications', { ids: [n.id] });
      toast(removedText(answer.deleted, 'one'), 'ok');
      refresh();
    } catch (e) {
      setRemoved((all) => {
        const back = new Set(all);
        back.delete(n.id);
        return back;
      });
      toast(errorMessage(e), 'bad');
    }
  };
  /** "Okunanları sil" right away; "Tümünü sil" asks first, with how many there are now. */
  const clear = async (onlyRead: boolean) => {
    setBusy(true);
    try {
      if (!onlyRead) {
        const now = await api<NotificationCounts>('GET', '/api/notifications/count');
        if (now.total === 0) {
          toast(removedText(0, 'all'), 'info');
          refresh();
          return;
        }
        if (!window.confirm(clearAllQuestion(now.total))) return;
      }
      const answer = await api<NotificationsRemoved>('POST', '/api/notifications/clear', { onlyRead });
      toast(removedText(answer.deleted, onlyRead ? 'read' : 'all'), 'ok');
      refresh();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="row notice-bar">
        <div className="tabs" role="tablist">
          <button className={!unreadOnly ? 'on' : ''} role="tab" aria-selected={!unreadOnly} onClick={() => setUnreadOnly(false)}>
            Tümü
          </button>
          <button className={unreadOnly ? 'on' : ''} role="tab" aria-selected={unreadOnly} onClick={() => setUnreadOnly(true)}>
            Okunmamış
          </button>
        </div>
        <span className="spacer" style={{ flex: 1 }} />
        <a className="btn ghost notice-settings" href="#/ayarlar" title="Bildirim ayarları" aria-label="Bildirim ayarları">
          <Icon name="sliders" size={18} /> <span>Bildirim ayarları</span>
        </a>
      </div>
      <div className="row notice-actions">
        <button className="btn" disabled={unread === 0 || busy} onClick={() => void markAll()}>
          <Icon name="check" size={18} /> Tümünü okundu say
        </button>
        <button className="btn" disabled={total - unread <= 0 || busy} onClick={() => void clear(true)}>
          <Icon name="trash" size={18} /> Okunanları sil
        </button>
        <button className="btn danger-text" disabled={total === 0 || busy} onClick={() => void clear(false)}>
          <Icon name="trash" size={18} /> Tümünü sil
        </button>
      </div>
      <section className="card">
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!shown ? (
          <Spinner />
        ) : shown.length === 0 ? (
          <Empty title={unreadOnly ? 'Okunmamış bildirim yok' : 'Henüz bildirim yok'}>
            Gönderiler, siparişler, pazaryeri hesapları ve faturalarla ilgili gelişmeler burada görünür.
          </Empty>
        ) : (
          <div className="notice-list">
            {shown.map((n) => (
              <div key={n.id} className={`notice-row${n.readAt ? '' : ' unread'}`}>
                <button className="notice" onClick={() => void open(n)}>
                  <span className="notice-icon">
                    <Icon name={ICONS[n.kind.split('.')[0]!] ?? 'info'} size={18} />
                  </span>
                  <span className="notice-text">
                    <b>{n.title}</b>
                    {n.body && <span className="muted">{n.body}</span>}
                    <small className="muted">
                      {fmt.age(n.createdAt)} önce · {NOTIFICATION_KINDS[n.kind]?.label ?? n.kind}
                      {n.tenantCode && (
                        <>
                          {' '}
                          · <SellerChip code={n.tenantCode} />
                        </>
                      )}
                    </small>
                  </span>
                  {n.link && (
                    <span className="notice-go" aria-hidden>
                      <Icon name="back" size={16} />
                    </span>
                  )}
                </button>
                <button className="icon-btn notice-del" title="Bildirimi sil" aria-label={`Bildirimi sil: ${n.title}`} onClick={() => void remove(n)}>
                  <Icon name="trash" size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/** Ayarlar → Bildirimler: which kinds reach this person. Floor staff get none, so they see nothing here. */
export function NotificationSettings() {
  const toast = useToast();
  const settings = useLoad(() => api<NotificationSetting[]>('GET', '/api/notifications/settings'), []);
  const [busy, setBusy] = useState<string | null>(null);
  if (settings.error) return <ErrorBanner message={settings.error} onRetry={() => void settings.reload()} />;
  if (!settings.data || settings.data.length === 0) return null;
  const toggle = async (s: NotificationSetting) => {
    setBusy(s.kind);
    try {
      await api('PUT', '/api/notifications/settings', { kind: s.kind, on: !s.on });
      await settings.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="bell" />
        <h2>Bildirimler</h2>
        <span className="sub">Hangi gelişmeler size bildirilsin; bildirimler sağ üstteki zilde toplanır</span>
      </div>
      <div className="card-b stack">
        {settings.data.map((s) => (
          <label key={s.kind} className="row" style={{ gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={s.on} disabled={busy === s.kind} onChange={() => void toggle(s)} style={{ marginTop: 3 }} />
            <span style={{ flex: '1 1 0', minWidth: 0 }}>
              <b style={{ fontWeight: 600 }}>{s.label}</b>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                {s.hint}
              </span>
            </span>
          </label>
        ))}
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Ayar yalnızca sizin için geçerlidir; diğer kullanıcılar kendi bildirimlerini ayrıca seçer.
        </p>
      </div>
    </section>
  );
}
